import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, Upload, FileVideo, ImageIcon, X,
  Loader2, CheckCircle2, AlertTriangle, Receipt,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { IdeaLogoBadge } from "../../components/IdeaLogoBadge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { fmtDate } from "../../lib/statuses";

const STEPS = ["Details", "Problem", "Evidence"];

function Stepper({ step }) {
  return (
    <div className="mb-8 flex items-center justify-between">
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <div className="flex flex-col items-center gap-1.5">
            <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
              i < step ? "bg-emerald-500 text-white" : i === step ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"
            }`}>
              {i < step ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`text-xs font-medium ${i === step ? "text-blue-600" : "text-slate-400"}`}>{label}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`mx-2 h-0.5 flex-1 ${i < step ? "bg-emerald-500" : "bg-slate-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function FileDrop({ label, accept, file, onFile, testId, icon: Icon = Upload, hint }) {
  return (
    <div>
      <Label className="mb-1.5 block text-sm font-medium">{label}</Label>
      {!file ? (
        <label data-testid={testId} className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center transition-colors hover:border-blue-400 hover:bg-blue-50">
          <Icon className="mb-2 h-6 w-6 text-slate-400" />
          <span className="text-sm font-medium text-slate-600">Tap to upload</span>
          {hint && <span className="mt-0.5 text-xs text-slate-400">{hint}</span>}
          <input type="file" accept={accept} className="hidden" onChange={(e) => onFile(e.target.files[0])} />
        </label>
      ) : (
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <Icon className="h-5 w-5 shrink-0 text-blue-600" />
            <span className="truncate text-sm text-slate-700">{file.name}</span>
          </div>
          <button type="button" onClick={() => onFile(null)} className="text-slate-400 hover:text-rose-500"><X className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}

export default function WarrantyClaimForm() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    order_number: "", customer_name: "", mobile: "", product_name: "", brand: "",
    serial_number: "", problem_description: "",
  });
  const [serialPhoto, setSerialPhoto] = useState(null);
  const [problemVideo, setProblemVideo] = useState(null);
  const [invoicePhoto, setInvoicePhoto] = useState(null);
  const [extraPhotos, setExtraPhotos] = useState([]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validateStep = () => {
    if (step === 0) {
      if (!form.order_number || !form.customer_name || !form.mobile || !form.product_name || !form.serial_number) {
        toast.error("Please complete all required (*) fields"); return false;
      }
    }
    return true;
  };
  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, 2)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!serialPhoto) { toast.error("Serial number photo is required"); return; }
    if (!problemVideo) { toast.error("Problem video is required"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify(form));
      fd.append("serial_photo", serialPhoto);
      fd.append("problem_video", problemVideo);
      if (invoicePhoto) fd.append("invoice_photo", invoicePhoto);
      extraPhotos.forEach((p) => fd.append("additional_photos", p));
      const { data } = await api.post("/warranty/claims", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      setResult(data);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const detailText = (formatApiError(detail) || "").toLowerCase();
      const oversizedVideo =
        status === 413 ||
        (status === 400 && detailText.includes("video") && detailText.includes("exceed"));
      if (oversizedVideo) {
        toast.error("Video file is too large. Please upload a smaller video.");
      } else {
        toast.error(formatApiError(detail) || "Submission failed");
      }
    } finally { setSubmitting(false); }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-12">
        <div className="mx-auto max-w-lg animate-fade-up rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-9 w-9" /></div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Claim Submitted!</h1>
          <p className="mt-1 text-slate-500">Save your RMA number to track your claim.</p>
          <div className="my-6 rounded-2xl border border-blue-200 bg-blue-50 p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-blue-600">RMA Number</div>
            <div data-testid="result-rma" className="font-mono text-2xl font-bold text-slate-900">{result.rma_number}</div>
          </div>
          <div className="space-y-2 text-left text-sm">
            <Row label="Product" value={result.product_name} />
            <Row label="Serial Number" value={result.serial_number} />
            <Row label="Submission Date" value={fmtDate(result.created_at)} />
            <Row label="Current Status" value={result.status} />
          </div>
          <div className="mt-6 flex gap-3">
            <Link to="/warranty/track" className="flex-1"><Button data-testid="go-track-btn" className="w-full bg-blue-600 hover:bg-blue-700">Track Claim</Button></Link>
            <Link to="/warranty" className="flex-1"><Button variant="outline" className="w-full">Done</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-2xl">
        <Link to="/warranty" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Back to portal</Link>
        <div className="mb-6 flex items-center gap-3">
          <IdeaLogoBadge />
          <h1 className="font-display text-xl font-bold text-slate-900">New Warranty Claim</h1>
        </div>

        {/* RED warning */}
        <div data-testid="physical-warning" className="mb-5 flex items-start gap-3 rounded-xl border-2 border-rose-300 bg-rose-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
          <div>
            <div className="font-display text-sm font-extrabold uppercase tracking-wide text-rose-700">Important Note</div>
            <div className="text-sm font-semibold text-rose-700">NO WARRANTY FOR PHYSICAL ISSUE</div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <Stepper step={step} />

          {step === 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Order / Invoice Number *" testId="input-order-number" value={form.order_number} onChange={set("order_number")} />
              <Field label="Customer Name *" testId="input-name" value={form.customer_name} onChange={set("customer_name")} />
              <Field label="WhatsApp Number *" testId="input-mobile" value={form.mobile} onChange={set("mobile")} placeholder="+968 ..." />
              <Field label="Product Name *" testId="input-product" value={form.product_name} onChange={set("product_name")} />
              <Field label="Brand" testId="input-brand" value={form.brand} onChange={set("brand")} />
              <Field label="Serial Number *" testId="input-serial" value={form.serial_number} onChange={set("serial_number")} />
            </div>
          )}

          {step === 1 && (
            <div>
              <Label className="mb-1.5 block text-sm font-medium">Problem Description</Label>
              <Textarea data-testid="input-problem-description" rows={6} value={form.problem_description} onChange={set("problem_description")} placeholder="Describe the issue in detail..." />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <FileDrop label="Serial Number Photo *" accept="image/*,.heic,.heif" file={serialPhoto} onFile={setSerialPhoto} testId="upload-serial-photo" icon={ImageIcon} hint="Clear photo of the product serial number" />
              <FileDrop label="Problem Video *" accept="video/mp4,video/quicktime,.mov" file={problemVideo} onFile={setProblemVideo} testId="upload-problem-video" icon={FileVideo} hint="MP4 or MOV demonstrating the issue" />
              <FileDrop label="Invoice Photo" accept="image/*,.heic,.heif" file={invoicePhoto} onFile={setInvoicePhoto} testId="upload-invoice-photo" icon={Receipt} hint="Photo of your purchase invoice" />
              <div>
                <Label className="mb-1.5 block text-sm font-medium">Additional Photos (optional)</Label>
                <label data-testid="upload-extra-photos" className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-sm text-slate-500 hover:border-blue-400 hover:bg-blue-50">
                  <Upload className="h-4 w-4" /> Add more photos
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setExtraPhotos([...extraPhotos, ...Array.from(e.target.files)])} />
                </label>
                {extraPhotos.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {extraPhotos.map((p, i) => (
                      <span key={i} className="flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-600">{p.name} <X className="h-3 w-3 cursor-pointer" onClick={() => setExtraPhotos(extraPhotos.filter((_, idx) => idx !== i))} /></span>
                    ))}
                  </div>
                )}
              </div>
              {submitting && progress > 0 && (
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-blue-600 transition-all" style={{ width: `${progress}%` }} /></div>
              )}
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button variant="outline" onClick={back} disabled={step === 0 || submitting} data-testid="form-back-btn" className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Back</Button>
            {step < 2 ? (
              <Button onClick={next} data-testid="form-next-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700">Next <ArrowRight className="h-4 w-4" /></Button>
            ) : (
              <Button onClick={submit} disabled={submitting} data-testid="form-submit-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700">
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</> : <>Submit Claim <Check className="h-4 w-4" /></>}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <div>
      <Label className="mb-1.5 block text-sm font-medium">{label}</Label>
      <Input data-testid={testId} {...props} />
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-slate-100 py-1.5">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
}
